export class AssetHandler {
  constructor(canvas, bounds, assets) {
    this.assetCache = new Map();
    this.canvas = canvas;
    this.bounds = bounds;
    this.assets = assets;
    this.imageOutputDir = 'images';
    this.exportedImages = new Map();
  }

  async extractAssets() {
    try {
      const processedAssets = await Promise.all(
        this.assets.map((asset) => this.processAsset(asset)),
      );

      return processedAssets.filter(Boolean);
    } catch (error) {
      console.error('Error extracting SVG assets:', error);
      throw error;
    }
  }

  async processAsset(asset) {
    try {
      const format = this.getFormat(asset.src);

      if (format !== 'svg') {
        const { content, externalResources } = await this.processRasterImg(
          asset.object,
          asset.src,
          format,
        );
        return {
          content,
          externalResources,
          zIndex: asset.zIndex,
        };
      }

      // For SVG assets, use Fabric.js toSVG method
      const svgContent = await this.getFabricSVG(asset.object);
      return {
        content: svgContent,
        zIndex: asset.zIndex,
      };
    } catch (error) {
      console.error('Error processing SVG asset:', asset.url, error);
      return null;
    }
  }

  async getSvgContent(src) {
    if (src.startsWith('data:')) {
      const base64Content = src.split(',')[1];
      return atob(base64Content);
    }

    if (this.assetCache.has(src)) {
      return this.assetCache.get(src);
    }

    try {
      const response = await fetch(src);
      if (!response.ok) throw new Error(`Failed fetching: ${src}`);

      const svgText = await response.text();
      this.assetCache.set(src, svgText);
      return svgText;
    } catch (error) {
      console.error('Error converting to Base64:', error);
    }
  }

  getFormat(src) {
    if (src.endsWith('.png')) return 'png';
    if (src.endsWith('.jpg') || src.endsWith('.jpeg')) return 'jpg';
    if (src.endsWith('.webp')) return 'webp';
    if (src.endsWith('.gif')) return 'gif';
    return 'svg';
  }

  async saveExternalImage(src, fabricObject, format = 'png') {
    const imageName = `image_${fabricObject.imgId || Date.now()}.${format}`;

    if (!this.exportedImages.has(src)) {
      const imageBlob = await this.getImageBlob(src);
      this.exportedImages.set(src, {
        name: imageName,
        blob: imageBlob,
      });
    }

    return this.exportedImages.get(src).name;
  }

  async getImageBlob(src, format = 'png') {
    if (src.startsWith('data:')) {
      const base64Data = src.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteArrays = [];

      for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
        const slice = byteCharacters.slice(offset, offset + 1024);
        const byteNumbers = new Array(slice.length);

        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }

        byteArrays.push(new Uint8Array(byteNumbers));
      }

      const formatsTypes = {
        png: 'image/png',
        jpg: 'image/jpeg',
        webp: 'image/webp',
        gif: 'image/gif',
      };

      return new Blob(byteArrays, { type: formatsTypes[format] });
    } else {
      const response = await fetch(src);
      return response.blob();
    }
  }

  async getFabricSVG(fabricObject) {
    // Adjust object position relative to canvas bounds
    const originalLeft = fabricObject.left;
    const originalTop = fabricObject.top;

    // Temporarily adjust position for SVG generation
    fabricObject.left = originalLeft - this.bounds.actualLeft;
    fabricObject.top = originalTop - this.bounds.actualTop;

    let svgContent = fabricObject.toSVG();

    // Fix opacity: ensure it's applied to the root group element
    const opacity = fabricObject.opacity !== undefined ? fabricObject.opacity : 1;

    // More robust opacity handling - find the first <g> tag and ensure opacity is set correctly
    let opacityApplied = false;
    svgContent = svgContent.replace(/<g(\s[^>]*)?>/, (match, attributes = '') => {
      if (opacityApplied) return match; // Only apply to the first <g> element

      opacityApplied = true;

      // Remove any existing opacity attribute (handle various spacing scenarios)
      let cleanAttributes = attributes.replace(/\s*opacity\s*=\s*"[^"]*"/gi, '');

      // Ensure proper spacing
      if (cleanAttributes && !cleanAttributes.startsWith(' ')) {
        cleanAttributes = ' ' + cleanAttributes;
      }

      // Add the correct opacity
      return `<g${cleanAttributes} opacity="${opacity}">`;
    });

    if (fabricObject.src) {
      // SVG image: fetch and embed vector paths directly
      let svgText = await this.getSvgContent(fabricObject.src);
      if (svgText) {
        // Extract vector paths and inherited styles
        const { content: vectorContent, rootStyle } = this.extractSVGVectorContent(
          svgText,
          fabricObject.width,
          fabricObject.height,
        );

        // Get original SVG dimensions from the source
        const originalDimensions = this.getOriginalSVGDimensions(svgText);

        svgContent = svgContent.replace(/xlink:href="([^"]+)"/g, '');
        svgContent = svgContent.replace(/<image([^>]*)\/?>/g, (match, attributes) => {
          const x = attributes.match(/x="([^"]*)"/)?.[1] || '0';
          const y = attributes.match(/y="([^"]*)"/)?.[1] || '0';
          const width = attributes.match(/width="([^"]*)"/)?.[1] || fabricObject.width;
          const height = attributes.match(/height="([^"]*)"/)?.[1] || fabricObject.height;

          const scaleX = originalDimensions.width ? width / originalDimensions.width : 1;
          const scaleY = originalDimensions.height ? height / originalDimensions.height : 1;

          // Re-apply root style (including fill) to preserve original color inheritance
          const styleAttr = rootStyle ? ` ${rootStyle}` : '';

          return `<g transform="translate(${x},${y}) scale(${scaleX}, ${scaleY})"${styleAttr}>
        ${vectorContent}
      </g>`;
        });
        svgContent = svgContent.replace(/<\/image>/g, '');
      }
    }

    // Fix conflicting gradient IDs by making them unique
    const uniqueId = fabricObject.imgId || Date.now() + Math.random();

    // First, find all IDs that need to be made unique
    const idMatches = svgContent.match(/id="([^"]*SVGID_[^"]*)"/g);
    if (idMatches) {
      idMatches.forEach((match) => {
        const originalId = match.match(/id="([^"]*)"/)[1];
        const newId = `${originalId}_${uniqueId}`;

        // Replace the ID definition
        svgContent = svgContent.replace(new RegExp(`id="${originalId}"`, 'g'), `id="${newId}"`);

        // Replace all references to this ID
        svgContent = svgContent.replace(
          new RegExp(`url\\(#${originalId}\\)`, 'g'),
          `url(#${newId})`,
        );
        svgContent = svgContent.replace(
          new RegExp(`xlink:href="#${originalId}"`, 'g'),
          `xlink:href="#${newId}"`,
        );
        svgContent = svgContent.replace(
          new RegExp(`href="#${originalId}"`, 'g'),
          `href="#${newId}"`,
        );
      });
    }

    // Restore original position
    fabricObject.left = originalLeft;
    fabricObject.top = originalTop;

    return svgContent;
  }

  extractSVGVectorContent(svgText, width, height) {
    const cleanSvg = svgText.replace(/<\?xml[^>]*\?>/, '').trim();
    const svgMatch = cleanSvg.match(/<svg[^>]*>([\s\S]*?)<\/svg>/i);
    if (!svgMatch) {
      // Fallback: no wrapper, return whole text
      return { content: cleanSvg, rootStyle: '' };
    }

    const fullSvgTagMatch = cleanSvg.match(/<svg[^>]*>/i);
    const svgTag = fullSvgTagMatch ? fullSvgTagMatch[0] : '';
    const innerContent = svgMatch[1];

    // Capture style or fill from root svg
    let rootStyle = '';
    const styleAttrMatch = svgTag.match(/style="([^"]*)"/i);
    if (styleAttrMatch) {
      // Keep entire style declaration
      rootStyle = `style="${styleAttrMatch[1]}"`;
    } else {
      const fillAttrMatch = svgTag.match(/fill="([^"]*)"/i);
      if (fillAttrMatch) {
        rootStyle = `fill="${fillAttrMatch[1]}"`;
      }
    }

    // Remove nested data-uri images to avoid loops
    const cleanedContent = innerContent.replace(/<image[^>]*xlink:href="data:[^"]*"[^>]*\/?>/g, '');

    return { content: cleanedContent, rootStyle };
  }

  async rasterizeSVG(svgText, width, height) {
    return new Promise((resolve, reject) => {
      // Clean SVG text
      const cleanSvg = svgText.replace(/<\?xml[^>]*\?>/, '').trim();

      // Create a blob from the SVG
      const svgBlob = new Blob([cleanSvg], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(svgBlob);

      // Create an image element
      const img = new Image();
      img.onload = () => {
        try {
          // Create canvas
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Set canvas dimensions
          canvas.width = width || img.naturalWidth || 100;
          canvas.height = height || img.naturalHeight || 100;

          // Draw the image to canvas
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          // Convert to base64 PNG
          const base64 = canvas.toDataURL('image/png');

          // Clean up
          URL.revokeObjectURL(url);
          resolve(base64);
        } catch (error) {
          URL.revokeObjectURL(url);
          reject(error);
        }
      };

      img.onerror = (error) => {
        URL.revokeObjectURL(url);
        reject(new Error(`Failed to load SVG: ${error}`));
      };

      img.src = url;
    });
  }

  async processRasterImg(fabricObject, src, format = 'png') {
    const imageBlob = await this.getImageBlob(src, format);

    // Use Fabric.js toSVG method for raster images too
    const originalSrc = fabricObject.src;

    // Convert blob to base64 data URI
    const base64Data = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(imageBlob);
    });

    // Adjust position relative to canvas bounds
    const originalLeft = fabricObject.left;
    const originalTop = fabricObject.top;
    fabricObject.left = originalLeft - this.bounds.actualLeft;
    fabricObject.top = originalTop - this.bounds.actualTop;

    let svgContent = fabricObject.toSVG();

    // Restore original values
    fabricObject.src = originalSrc;
    fabricObject.left = originalLeft;
    fabricObject.top = originalTop;

    // Embed base64 for raster images
    svgContent = svgContent.replace(/xlink:href="([^"]+)"/g, `xlink:href="${base64Data}"`);

    return {
      content: svgContent,
      externalResources: [],
    };
  }

  getExternalResources() {
    return Array.from(this.exportedImages.values());
  }

  extractSvgContent(svgString) {
    const cleanSvg = svgString.replace(/<\?xml[^>]*\?>/, '');
    const svgMatch = cleanSvg.match(/<svg([^>]*?)>([\s\S]*?)<\/svg>/i);
    if (!svgMatch) return cleanSvg;

    const [fullSvg, svgAttributes, innerContent] = svgMatch;

    const viewBoxMatch = fullSvg.match(/viewBox="([^"]*)"/);
    let viewBox = null;

    if (viewBoxMatch) {
      viewBox = viewBoxMatch[1];
    } else {
      // Extract width and height if viewBox is not present
      const widthMatch = svgAttributes.match(/width="([^"]*)"/);
      const heightMatch = svgAttributes.match(/height="([^"]*)"/);

      if (widthMatch && heightMatch) {
        const width = parseFloat(widthMatch[1]);
        const height = parseFloat(heightMatch[1]);

        if (!isNaN(width) && !isNaN(height)) {
          viewBox = `0 0 ${width} ${height}`;
        }
      }
    }

    // Use default viewBox if none was found
    if (!viewBox) {
      viewBox = '0 0 500 500';
    }

    const styleMatch = svgAttributes.match(/style="[^"]*fill:\s*([^;"]*)/) || [];
    const fillColor = styleMatch[1];

    return {
      viewBox,
      content: innerContent.trim(),
      fillColor,
    };
  }

  extractDefs(svgContent) {
    const defsMatch = svgContent.match(/<defs[^>]*>([\s\S]*?)<\/defs>/i);
    return defsMatch ? defsMatch[0] : '';
  }

  applySVGTransforms(svgContent, fabricObject) {
    const { viewBox, fillColor, content } = this.extractSvgContent(svgContent);
    const transforms = this.getTransformations(fabricObject);
    const fillStyle = fillColor ? `style="fill: ${fillColor}"` : '';

    // Handle clipPath if present
    let clipPathDef = '';
    let clipPathAttr = '';

    if (fabricObject.clipPath) {
      const clipId = `clip-${fabricObject.imgId || Date.now()}`;
      clipPathDef = this.createClipPathDef(fabricObject.clipPath, clipId, fabricObject);
      clipPathAttr = `clip-path="url(#${clipId})"`;
    }

    return {
      content: `
        <g
          transform="${transforms}"
          opacity="${fabricObject.opacity}"
          data-mtg-id="${fabricObject?.imgId || ''}"
          data-mtg-type="${fabricObject?.imgType || ''}"
          ${clipPathAttr}
        >
          <svg
            viewBox="${viewBox}"
            width="${fabricObject.width}"
            height="${fabricObject.height}"
            ${fillStyle}
            preserveAspectRatio="none"
          >
            ${content}
          </svg>
        </g>
      `,
      defs: clipPathDef,
    };
  }

  createClipPathDef(clipPath, clipId, parentObject) {
    if (!clipPath) return '';

    // Calculate the clipPath dimensions in the parent object's coordinate system
    const clipWidth = clipPath.width * (clipPath.scaleX || 1);
    const clipHeight = clipPath.height * (clipPath.scaleY || 1);
    const clipRadius = clipPath.radius * (clipPath.scaleX || 1);

    let clipElement = '';

    if (clipPath.type === 'rect') {
      // Position the clip rect relative to the parent object's top-left corner
      const x = clipPath.left || 0;
      const y = clipPath.top || 0;

      clipElement = `<rect x="${x}" y="${y}" width="${clipWidth}" height="${clipHeight}"/>`;
    } else if (clipPath.type === 'circle') {
      // Position the clip circle relative to the parent object's center
      const cx = clipPath.left || 0;
      const cy = clipPath.top || 0;

      clipElement = `<circle cx="${cx}" cy="${cy}" r="${clipRadius}"/>`;
    }

    return `<clipPath id="${clipId}">${clipElement}</clipPath>`;
  }

  clearCache() {
    this.assetCache.clear();
  }

  getOriginalSVGDimensions(svgText) {
    const cleanSvg = svgText.replace(/<\?xml[^>]*\?>/, '').trim();
    const svgMatch = cleanSvg.match(/<svg[^>]*>/i);

    if (!svgMatch) return { width: 444, height: 522 }; // fallback

    const svgTag = svgMatch[0];
    const widthMatch = svgTag.match(/width="([^"]*(?:px)?)"/) || svgTag.match(/width="([^"]*)"/);
    const heightMatch = svgTag.match(/height="([^"]*(?:px)?)"/) || svgTag.match(/height="([^"]*)"/);

    const width = widthMatch ? parseFloat(widthMatch[1]) : 444;
    const height = heightMatch ? parseFloat(heightMatch[1]) : 522;

    return { width, height };
  }
}

export default AssetHandler;
